/**
 * Resolves an opaque `media_assets` id to a fetchable URL for the ACTIVE
 * companion. The agent's `gallery` directive carries asset IDS only — the edge
 * never inlines URLs into a tool result — so anything rendering those ids needs a
 * lookup, or it renders `<img src={undefined}>`: a grid of broken images.
 *
 * The only place the edge presigns an asset is the Artifacts gallery read
 * (`GET /media/gallery?girlfriendId=…`), so this fetches that ONCE per companion
 * and indexes it by asset id — reuse of an existing read, no new backend surface
 * (same trade-off `companions/avatar-cache.ts` makes for avatars). Degrades to
 * `undefined` on a load failure or an unsignable asset (`url: null`) and never
 * throws; the caller shows a skeleton instead.
 */
import { isErr } from '@4f/shared';
import { createSignal } from 'solid-js';
import type { ApiClient } from '../../api/client.ts';
import type { MediaAssetView } from '../../api/types.ts';

/** Index a gallery listing by asset id, dropping the assets that could not be signed. */
export function indexAssetUrls(
  assets: readonly Pick<MediaAssetView, 'assetId' | 'url'>[],
): ReadonlyMap<string, string> {
  const index = new Map<string, string>();
  for (const asset of assets) {
    if (asset.url) {
      index.set(asset.assetId, asset.url);
    }
  }
  return index;
}

export interface AssetUrlCache {
  /** The resolved URL for an asset id, or `undefined` (not loaded / unsignable). */
  urlFor(assetId: string): string | undefined;
  /** Load (once) the companion's gallery so `urlFor` can resolve her assets. */
  ensure(companionId: string): void;
  /** Force a re-read — used when new media lands for the open companion. */
  refresh(companionId: string): void;
}

/** Build the cache over the injected client (DIP — no network in tests). */
export function createAssetUrlCache(api: ApiClient): AssetUrlCache {
  const [urls, setUrls] = createSignal<ReadonlyMap<string, string>>(new Map());
  const loaded = new Set<string>();

  const load = (companionId: string): void => {
    void api.listGallery(companionId).then((result) => {
      if (isErr(result)) {
        // Transient failure — forget it so a later `ensure` retries rather than
        // leaving every tile a permanent skeleton.
        loaded.delete(companionId);
        return;
      }
      const next = indexAssetUrls(result.value);
      // Merge rather than replace: a previously-open companion's ids may still be
      // on screen in replayed history, and dropping them would blank those tiles.
      setUrls((current) => new Map([...current, ...next]));
    });
  };

  return {
    urlFor: (assetId) => urls().get(assetId),
    ensure(companionId) {
      if (loaded.has(companionId)) {
        return;
      }
      loaded.add(companionId);
      load(companionId);
    },
    refresh(companionId) {
      loaded.add(companionId);
      load(companionId);
    },
  };
}
